import axios from "axios";
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import { identifyUser } from "~/shared/utils/analytics";
import {
  clearUserAndVisitCookies,
  setConsultAnswersJSON,
} from "~/visits/utils/visitCookies";

const SESSION_API_TOKEN_JWT = "apiTokenJWT";
const SESSION_API_TOKEN_RESTRICTED = "apiTokenRestricted";
const SESSION_USER_ID = "userID";
const SESSION_PORTAL_FIRST_NAME = "firstName";
const SESSION_PORTAL_LAST_NAME = "lastName";
const SESSION_GENDER = "gender";
const SESSION_EMAIL = "email";
const SESSION_PHONE = "phone";
const SESSION_US_STATE = "usState";

const COOKIE_EXPIRES_DEFAULT = 14;

export function setUserID(userID) {
  return setCookieWithExpiration(SESSION_USER_ID, userID);
}
export function getUserID() {
  return Cookies.get(SESSION_USER_ID);
}
export function getFirstName() {
  return Cookies.get(SESSION_PORTAL_FIRST_NAME);
}
export function setFirstName(firstName) {
  return setCookieWithExpiration(SESSION_PORTAL_FIRST_NAME, firstName);
}

export function getLastName() {
  return Cookies.get(SESSION_PORTAL_LAST_NAME);
}
export function setLastName(lastName) {
  return setCookieWithExpiration(SESSION_PORTAL_LAST_NAME, lastName);
}

export function getEmail() {
  return Cookies.get(SESSION_EMAIL);
}
export function setEmail(email) {
  return setCookieWithExpiration(SESSION_EMAIL, email);
}

export function setPhone(phone) {
  return setCookieWithExpiration(SESSION_PHONE, phone);
}
export function getPhone() {
  return Cookies.get(SESSION_PHONE);
}

export function getGender() {
  return Cookies.get(SESSION_EMAIL);
}
export function setGender(gender) {
  return setCookieWithExpiration(SESSION_GENDER, gender);
}

export function getUSState() {
  return Cookies.get(SESSION_US_STATE);
}
export function setUSState(usState) {
  return setCookieWithExpiration(SESSION_US_STATE, usState);
}

export function getAPITokenJWT() {
  // Weird caching bug? where using Cookies.get(SESSION_API_TOKEN_JWT ) incorrectly returns a cleared value
  // console.log("Cookies:", Cookies.get());
  return Cookies.get()[SESSION_API_TOKEN_JWT];
}

export function getAPITokenJWTRestricted() {
  // Weird caching bug? where using Cookies.get(SESSION_API_TOKEN_JWT ) incorrectly returns a cleared value
  // console.log("Cookies:", Cookies.get());
  const result = Cookies.get()[SESSION_API_TOKEN_RESTRICTED];
  if (!result || result === "false") {
    return false;
  }
  return true;
}

export function setAPITokenJWT(token) {
  return setCookieWithExpiration(SESSION_API_TOKEN_JWT, token);
}

export function setAPITokenJWTRestricted(value) {
  return setCookieWithExpiration(SESSION_API_TOKEN_RESTRICTED, value);
}

export function clearCookies() {
  Cookies.remove(SESSION_API_TOKEN_JWT);
  Cookies.remove(SESSION_API_TOKEN_RESTRICTED);
  Cookies.remove(SESSION_PORTAL_FIRST_NAME);
  Cookies.remove(SESSION_PORTAL_LAST_NAME);
  Cookies.remove(SESSION_EMAIL);

  clearUserAndVisitCookies();
}

export async function isMagicTokenLoggedIn() {
  const queryParams = new URLSearchParams(window.location.search);
  if (queryParams.has("ktoken")) {
    const ktoken = queryParams.get("ktoken");

    const kTokenStrippedURL = new URL(window.location);
    kTokenStrippedURL.searchParams.delete("ktoken");

    // ktoken is in querystring - try loggin in

    // strip ktoken and redirect

    // queryParams.delete("ktoken");

    await axios
      .post("/me/magic-login/", {
        ktoken: ktoken,
        url: kTokenStrippedURL, // Send url so new token can be emailed if failure
      })
      .then((response) => {
        const jsonData = response.data.data;

        const user = jsonData.find((item) => item.type === "user");
        const consultIntakes = jsonData.filter(
          (item) => item.type === "consultIntake"
        );
        const consultIntakesByConsultationType = {};

        consultIntakes.forEach((item) => {
          consultIntakesByConsultationType[
            item.attributes.consultationType +
              "-" +
              item.attributes.intakeVersion
          ] = item.attributes.intakeJSON;
        });

        const {
          apiTokenJWT,
          firstName,
          lastName,
          phone,
          gender,
          email,
          usState,
        } = user.attributes;

        clearCookies();

        setUserID(user.id);

        setAPITokenJWT(apiTokenJWT);
        setEmail(email);
        setPhone(phone);
        setGender(gender);
        setFirstName(firstName);
        setLastName(lastName);
        setUSState(usState);
        setAPITokenJWTRestricted(false);
        setConsultAnswersJSON(consultIntakesByConsultationType);

        if (user.id) {
          identifyUser(user.id);
        }

        if (apiTokenJWT) {
          // strip ?ktoken= querystring and redirect
          // Don't need to refresh
          // window.location = kTokenStrippedURL.toString();
          // window.location = "/me/messages/care";
        } else {
          console.error("Error: Invalid Login Token received");
        }
      })
      .catch(async (error) => {
        if (error.response) {
          console.error("Error logging in with ktoken:", error.response);
          // setLoginError(error.response.data.errors[0].title);
        } else {
          console.error("Error logging in:", error);
        }

        // if token expired and we have email, resend token with url (handled by backend, so just display error or redirect
        /*
          const targetLocation = kTokenStrippedURL.toString();

          if (error.response.data.errors[0].title) {
            window.location = targetLocation + `?loginError=${error.response.data.errors[0].title}`;
          } else {
            window.location = targetLocation;
          }
           */
      });
  }

  const token = getAPITokenJWT();
  // console.log("token: ", token);
  if (token != null) {
    try {
      const decoded = jwt_decode(token);
      const expDate = new Date(decoded.exp * 1000); // js uses nanoseconds, so need to x1000 to compare
      // console.log("logged in: ", expDate > new Date().getTime());
      // console.log("expDate:", expDate);
      // console.log(expDate > new Date().getTime());
      return expDate > new Date().getTime(); // logged in if expiration date is > now.
    } catch (error) {
      return false;
    }
  }

  return false;
}

export function isLoggedIn() {
  // Weird caching bug? where using Cookies.get(API_TOKEN ) incorrectly returns a cleared value
  // console.log("Cookies:", Cookies.get());
  const token = getAPITokenJWT();
  if (token != null) {
    try {
      const decoded = jwt_decode(token);
      const expDate = new Date(decoded.exp * 1000); // js uses nanoseconds, so need to x1000 to compare
      // console.log("logged in: ", expDate > new Date().getTime());
      // console.log("expDate:", expDate);
      // console.log(expDate > new Date().getTime());
      return expDate > new Date().getTime(); // logged in if expiration date is > now.
    } catch (error) {
      return false;
    }
  }
  return false;
}

// persistent cookie with expiration
function setCookieWithExpiration(key, value, expires = null) {
  if (expires == null) {
    return Cookies.set(key, value, { expires: COOKIE_EXPIRES_DEFAULT });
  } else {
    return Cookies.set(key, value, { expires: expires });
  }
}

export async function checkRestrictedTokenAndSendEmail() {
  const isRestricted = getAPITokenJWTRestricted();
  if (!isRestricted) {
    return false;
  }
  await axios.post("/me/send-magic-link/", {
    email: getEmail(),
    url: window.location.href, // Send url so new token can be emailed if failure
  });
  return true;
}
